import { Box, Button, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import AddLinkIcon from '@mui/icons-material/AddLink';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { tokens } from '../../../global/theme/tokens';
import useFetch from '../../../api/useFetch';
import { API_GET, API_POST, DBRequest, useConfig } from '../../../api/api';
import CardGrid from '../../../components/cards/layout/CardGrid';
import CardContainer from '../../../components/cards/layout/CardContainer';
import FilePreviewCard from '../../../components/cards/FilePreviewCard';
import MoneyLabel from '../../../components/label/MoneyLabel';
import DateLabel from '../../../components/label/DateLabel';
import SkeletonCard from '../../../components/cards/SkeletonCard';
import { NewTransactionRelation } from '../../../components/transactions/relations/NewTransactionRelation';
import { TravelExpensePreviewElement } from '../../../components/transactions/preview-elements/TravelExpensePreviewElement';
import { TravelExpenseRequestDetails } from '../../new-requests/extended-request-versions/ExtendTravelExpense';

/**
 * Read-only detail view of a reimbursement: the generated document, the money
 * side (assigned bank transactions, open amount) and every report it pays out —
 * travel log, day table and additional costs with their uploaded receipts.
 * The only action here is assigning or detaching transactions.
 */
export const ExpandReimbursement = ({ row, onUpdate }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  // The list keeps its payload small, so the reports are fetched on expand.
  const { apiData, setApiData } = useFetch(
    `travel-expense-reimbursements/${row.id}`,
  );
  const details = apiData?.data;

  const [assignOpen, setAssignOpen] = useState(false);

  function handleAssigned(updatedElement) {
    setApiData((prev) => ({
      ...prev,
      data: { ...prev.data, ...updatedElement },
    }));
    onUpdate({ ...row, ...updatedElement });
  }

  function handleDetach(transaction) {
    DBRequest({
      config,
      path: `transactions/${transaction.id}/relations`,
      method: API_POST({
        relationType: 'travelExpenseReimbursement',
        relations: [row.id],
        mode: 'remove',
      }),
      onResponse: () => {
        // After a remove the reimbursement is absent from the relation
        // response, so the detail endpoint delivers the fresh state.
        DBRequest({
          config,
          path: `travel-expense-reimbursements/${row.id}`,
          method: API_GET,
          onResponse: (res) => handleAssigned(res.data),
        });
      },
    });
  }

  if (!details) {
    return (
      <CardGrid>
        <SkeletonCard height={200} />
      </CardGrid>
    );
  }

  const transactions = details.transactions || [];

  return (
    <Box className="flex flex-col gap-4 w-full">
      <CardGrid>
        <FilePreviewCard file={details.file} />
        <CardContainer span={2}>
          <Box className="flex flex-col gap-1 w-full px-2">
            <Row
              label={t('Period')}
              value={
                <span className="monospace">
                  <DateLabel date={details.startDate} /> -{' '}
                  <DateLabel date={details.endDate} />
                </span>
              }
            />
            <Row
              label={t('Trips')}
              value={(details.travelExpenses || []).length}
            />
            <Row
              label={t('Brutto Amount')}
              value={<MoneyLabel money={details.amount} />}
            />
            <Row
              label={t('Open')}
              value={
                <Typography
                  component="span"
                  sx={{
                    color:
                      Number(details.openAmount) === 0
                        ? colors.success
                        : colors.error,
                    fontWeight: 600,
                  }}
                >
                  <MoneyLabel money={details.openAmount} />
                </Typography>
              }
            />
          </Box>
        </CardContainer>
        <CardContainer span={2}>
          <Box className="flex flex-col gap-2 w-full px-2">
            <Typography variant="h6" className="opacity-70">
              {t('Assigned Transactions')}
            </Typography>
            {transactions.length === 0 && (
              <Typography variant="body2" className="opacity-60">
                {t('No data available')}
              </Typography>
            )}
            {transactions.map((transaction) => (
              <Box
                key={transaction.id}
                className="flex items-center gap-2 rounded-lg px-2 py-1"
                sx={{ backgroundColor: colors.bgInput }}
              >
                <Box className="flex flex-col overflow-hidden">
                  <Typography variant="body2" noWrap>
                    {transaction.transactionPartnerName ||
                      transaction.bookingText}
                  </Typography>
                  <Typography variant="caption" className="opacity-60 monospace">
                    <DateLabel date={transaction.bookingDate} />
                  </Typography>
                </Box>
                <Typography className="ml-auto monospace whitespace-nowrap">
                  <MoneyLabel money={transaction.assignedAmount} />
                </Typography>
                <Button
                  size="small"
                  color="error"
                  startIcon={<LinkOffIcon />}
                  onClick={() => handleDetach(transaction)}
                >
                  {t('Detach')}
                </Button>
              </Box>
            ))}
            {Number(details.openAmount) !== 0 && (
              <Button
                startIcon={<AddLinkIcon />}
                onClick={() => setAssignOpen(true)}
              >
                {t('Assign Transaction')}
              </Button>
            )}
          </Box>
        </CardContainer>
      </CardGrid>

      {(details.travelExpenses || []).map((trip) => (
        <Box
          key={trip.id}
          className="flex flex-col gap-2 rounded-lg p-3"
          sx={{
            backgroundColor: colors.bgInput + '60',
            border: `1px solid ${colors.selected}`,
          }}
        >
          <Box className="flex items-center gap-2 flex-wrap">
            <Typography variant="h6">
              {trip.project?.name || t('Travel Expense')}
            </Typography>
            <Typography variant="body2" className="opacity-60 monospace">
              {moment(trip.startTime).format('DD.MM.YYYY')}
            </Typography>
            <Typography className="ml-auto monospace font-bold">
              <MoneyLabel money={trip.totalPrice} />
            </Typography>
          </Box>
          <TravelExpenseRequestDetails
            request={{ travelExpense: trip, comment: trip.comment }}
          />
        </Box>
      ))}

      <NewTransactionRelation
        open={assignOpen}
        RelationPreview={TravelExpensePreviewElement}
        selectedElement={details}
        onClose={() => setAssignOpen(false)}
        relationType="travelExpenseReimbursement"
        transactionArrayKey="travelExpenseReimbursements"
        onUpdate={(updatedElement) => {
          setAssignOpen(false);
          handleAssigned(updatedElement);
        }}
      />
    </Box>
  );
};

const Row = ({ label, value }) => (
  <Box className="flex flex-row gap-2 items-center justify-between">
    <Typography variant="body2" className="opacity-70">
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {value}
    </Typography>
  </Box>
);
